import { Challenge, GameRole, Skill, CareerLevel } from '../types/game';
import { challenges } from '../data/challenges';

// Get a random challenge that's appropriate for the player's level and skills
export const getRandomChallenge = (
  excludeIds: string[] = [], 
  careerLevel: CareerLevel = 'Junior',
  playerSkills: Skill[] = [],
  yearsPassed: number = 0,
  recentChallenges: string[] = []
): Challenge => {
  // Filter challenges by difficulty tier appropriate for career level
  const difficultyMapping = {
    'Junior': [1, 2],         // Junior gets easier challenges
    'Mid-Level': [1, 2, 3],
    'Senior': [2, 3, 4],
    'Leader': [3, 4, 5],
    'Expert': [4, 5]
  };
  
  // Add progression - allow higher difficulties as years pass
  let appropriateDifficulties = difficultyMapping[careerLevel] || [1, 2, 3, 4, 5];
  
  // After 3 years (reduced from 5), allow one higher difficulty level
  if (yearsPassed >= 3) {
    const maxCurrentDifficulty = Math.max(...appropriateDifficulties);
    if (maxCurrentDifficulty < 5) {
      appropriateDifficulties.push(maxCurrentDifficulty + 1);
    }
  }
  
  // Combine exclude IDs with recent challenges to prevent repetition
  const allExcludeIds = [...excludeIds, ...recentChallenges];
  
  console.log('Filtering challenges with exclude IDs:', allExcludeIds);
  
  // Filter challenges that are:
  // 1. Not in the exclude list or recent challenges
  // 2. Appropriate difficulty for career level
  // 3. Don't require skills the player doesn't have
  let availableChallenges = challenges.filter(challenge => {
    // Check if challenge is in exclude list or recent challenges
    if (allExcludeIds.includes(challenge.id)) {
      return false;
    }
    
    // Check if challenge difficulty is appropriate
    if (!appropriateDifficulties.includes(challenge.difficulty)) {
      return false;
    }
    
    // Check if player has required skills
    if (challenge.requiredSkills && challenge.requiredSkills.length > 0) {
      const hasRequiredSkills = challenge.requiredSkills.every(
        skillId => playerSkills.some(playerSkill => playerSkill.id === skillId)
      );
      if (!hasRequiredSkills) {
        return false;
      }
    }
    
    return true;
  });
  
  // Prioritize challenges that require skills the player has (more relevant challenges)
  const challengesWithRelevantSkills = availableChallenges.filter(challenge => 
    challenge.requiredSkills && challenge.requiredSkills.length > 0 &&
    challenge.requiredSkills.some(skillId => 
      playerSkills.some(playerSkill => playerSkill.id === skillId)
    )
  );
  
  // 80% chance to select a challenge that matches player skills if available
  if (challengesWithRelevantSkills.length > 0 && Math.random() < 0.8) {
    availableChallenges = challengesWithRelevantSkills;
  }
  
  // If no appropriate challenges, fall back to basic challenges
  if (availableChallenges.length === 0) {
    console.log('No appropriate challenges found, falling back to basic challenges');
    const basicChallenges = challenges.filter(c => 
      !allExcludeIds.includes(c.id) && c.difficulty <= 2 && (!c.requiredSkills || c.requiredSkills.length === 0)
    );
    
    if (basicChallenges.length > 0) {
      return basicChallenges[Math.floor(Math.random() * basicChallenges.length)];
    }
    
    // Last resort: return any non-excluded challenge
    const anyChallenges = challenges.filter(c => !allExcludeIds.includes(c.id));
    return anyChallenges[Math.floor(Math.random() * anyChallenges.length)];
  }
  
  return availableChallenges[Math.floor(Math.random() * availableChallenges.length)];
};

export const getRandomChallengeForRole = (
  role: GameRole, 
  careerLevel: CareerLevel = 'Junior',
  playerSkills: Skill[] = [],
  yearsPassed: number = 0,
  recentChallenges: string[] = []
): Challenge => {
  // CRITICAL FIX: Add debugging to track challenge selection
  console.log('Getting role-specific challenge for:', role.id, 'Career level:', careerLevel);
  console.log('Recent challenges:', recentChallenges);
  
  // Get available role-specific challenges
  const roleSpecificChallenges = role.specialChallenges.filter(challenge => {
    // Check if challenge is in recent challenges list
    if (recentChallenges.includes(challenge.id)) {
      console.log(`Skipping challenge ${challenge.id} - recently used`);
      return false;
    }
    
    // Check if challenge difficulty is appropriate
    const difficultyMapping = {
      'Junior': [1, 2],         // Junior gets easier challenges
      'Mid-Level': [1, 2, 3],
      'Senior': [2, 3, 4],
      'Leader': [3, 4, 5],
      'Expert': [4, 5]
    };
    
    let appropriateDifficulties = difficultyMapping[careerLevel] || [1, 2, 3, 4, 5];
    
    // After 3 years (reduced from 5), allow one higher difficulty level
    if (yearsPassed >= 3) {
      const maxCurrentDifficulty = Math.max(...appropriateDifficulties);
      if (maxCurrentDifficulty < 5) {
        appropriateDifficulties.push(maxCurrentDifficulty + 1);
      }
    }
    
    if (!appropriateDifficulties.includes(challenge.difficulty)) {
      console.log(`Skipping challenge ${challenge.id} - wrong difficulty: ${challenge.difficulty}`);
      return false;
    }
    
    // Check if player has required skills
    if (challenge.requiredSkills && challenge.requiredSkills.length > 0) {
      const hasRequiredSkills = challenge.requiredSkills.every(
        skillId => playerSkills.some(playerSkill => playerSkill.id === skillId)
      );
      if (!hasRequiredSkills) {
        console.log(`Skipping challenge ${challenge.id} - missing required skills`);
        return false;
      }
    }
    
    return true;
  });
  
  console.log('Available role-specific challenges:', roleSpecificChallenges.length);
  
  // CRITICAL FIX: Dramatically increase the probability of role-specific challenges for Junior level
  // This ensures Junior Technicians get technician-specific challenges
  const roleSpecificProbability = {
    'Junior': 0.95,     // Further increased to 95% for Junior level
    'Mid-Level': 0.8,   // Increased for better role relevance
    'Senior': 0.7,
    'Leader': 0.6,
    'Expert': 0.6
  };
  
  const threshold = roleSpecificProbability[careerLevel] || 0.95; // Default to 95% for Junior
  
  // Randomly decide whether to use a role-specific challenge
  if (roleSpecificChallenges.length > 0 && Math.random() < threshold) {
    // For Junior level, prioritize the easiest challenges
    if (careerLevel === 'Junior') {
      const easyChallenges = roleSpecificChallenges.filter(c => c.difficulty <= 2);
      if (easyChallenges.length > 0) {
        const selectedChallenge = easyChallenges[Math.floor(Math.random() * easyChallenges.length)];
        console.log('Selected Junior easy challenge:', selectedChallenge.id);
        return selectedChallenge;
      }
    }
    
    const selectedChallenge = roleSpecificChallenges[Math.floor(Math.random() * roleSpecificChallenges.length)];
    console.log('Selected role-specific challenge:', selectedChallenge.id);
    return selectedChallenge;
  }
  
  console.log('Using general challenge pool');
  // Otherwise use a general challenge
  return getRandomChallenge(
    role.specialChallenges.map(c => c.id), // Exclude special challenges from general pool 
    careerLevel,
    playerSkills,
    yearsPassed,
    recentChallenges
  );
};

// Calculate the optimal decision frequency based on career level and experience
export const getDecisionFrequency = (careerLevel: CareerLevel, experience: number, yearsPassed: number): number => {
  // Significantly lower base frequencies to avoid clustering
  const baseFrequency = {
    'Junior': 0.08,      // ~1 decision per year
    'Mid-Level': 0.12,   // ~1-2 decisions per year
    'Senior': 0.15,      // ~1-2 decisions per year
    'Leader': 0.18,      // ~2 decisions per year
    'Expert': 0.20       // ~2 decisions per year
  };
  
  // Reduce experience factor to prevent rapid scaling
  const experienceFactor = Math.min(0.05, experience / 2000);
  
  // Add very small year factor for long-term play
  const yearFactor = Math.min(0.03, yearsPassed / 30);
  
  return (baseFrequency[careerLevel] || 0.08) + experienceFactor + yearFactor;
};
